/**
 * FP-01 (E02-F07-T01) — Stage-4 "Launched" delivery surface.
 *
 * v1 parity (`qa/screenshots/fidelity-2026-07/v1/admin/06-project-launched.png`):
 * the final funnel step is the "Package Ready for Delivery" hand-off screen, NOT
 * the Research library. The audit (`qa/fidelity-visual-comparison-2026-07.md`
 * P0-1) flagged that v2 mis-mounted `ResearchLibraryView` on Stage-4, so the
 * real delivery step was missing entirely.
 *
 * PK-QA-36 — Launched is now a faithful, TERMINAL read-only mirror of the
 * Generation surface: the SAME 6 documents (single source of truth
 * `GENERATION_CARDS`, so the doc set can never drift the way the old hardcoded
 * `LAUNCHED_PACKAGE_DOCS = [jd_doc, interview_questions, inmails]` list did — the
 * PK-QA-31 "two divergent lists" class of bug). Every doc is individually
 * previewable (read-only, host mounts `DocumentView`) + downloadable, plus a
 * "Download All" that sequentially exports every existing doc. There is NO
 * per-card Send here (individual sends already happened during Generation); the
 * banner's "Send the final package" wiring lands in a follow-up pass.
 *
 * Download is available for ALL 6 types unconditionally (including the
 * communication docs `kickoff_sheet` / `client_followup_email`) — Launched is a
 * terminal archive where everything is individually downloadable, so this does
 * NOT reuse the Generation `cardActionsFor` gating (`status==='approved' && !comm`
 * would hide Download for the comm docs, per PK-QA-36 §3).
 *
 * This component is presentational: the host (`ProjectDetailView` →
 * `LaunchedStageBody`) owns the read-only preview dialog and the `.doc` export,
 * mirroring how the Generation stage wires its dialogs.
 */
import type { JSX } from 'react';
import { GENERATION_LABELS, richTextToPlainText, stripWrappingCodeFence } from '@eer/shared';
import {
  GENERATION_CARDS,
  artifactStatusOf,
  badgeFor,
  type ArtifactMap,
} from './docGenerationModel.js';

export interface LaunchedStageViewProps {
  /** Current artifacts keyed by type (from the project detail card). */
  readonly artifacts: ArtifactMap;
  /** Open the read-only preview for a doc type (host owns the dialog). */
  readonly onPreview: (type: string) => void;
  /** Export the doc as a `.doc` file (host owns downloadArtifactDoc). */
  readonly onDownload: (type: string) => void;
  /**
   * "Send Final Package" — the host generates the R2 links then opens the send
   * dialog pre-composed with them (PK-QA-36 §5). Makes the banner's promise real.
   */
  readonly onSendPackage: () => void;
  /** True while the package-links request is in flight (disables the button + spinner). */
  readonly sendingPackage?: boolean;
}

/**
 * Extract a short, safe plain-text preview from an artifact's `content` (which is
 * a free-form jsonb object whose shape varies per doc type). Walks string leaves
 * only and never emits `undefined` / `null` / `[object Object]` (QA asserts no
 * such placeholder ever leaks into the UI). Empty content → '' (the card shows a
 * neutral fallback instead).
 *
 * Каждый строковый лист проходит через `richTextToPlainText` + снятие
 * markdown-ограждения. Это не украшательство: после Этапа 4 (KS-07) артефакты
 * хранятся как HTML, и на живом dev карточка показывала рекрутёру
 * ```` ```html <p>Dear [FIRST NAME],</p> <b>Client Company:</b> ```` —
 * то есть ровно ту «видимую разметку», из-за которой этап и начался. Превью
 * обрезается по символам (140), поэтому теги ещё и съедали бюджет и могли
 * оборваться на середине (`<stro`).
 *
 * E03-F08-T05 — карточка Kickoff Sheet показывала `image001[79].png: https://…r2…`:
 * jsonb ставит короткий ключ `jd_copy` раньше `kickoff_sheet`, а в `jd_copy`
 * лежали строки «файл: url». Поэтому вложенные приложения (`jd_copy`,
 * `sourcing_sheet`) на верхнем уровне читаются только когда у самого документа
 * текста нет, а ссылки («файл: url» и голые URL) из превью вырезаются всегда.
 */
const PREVIEW_APPENDIX_KEYS = new Set(['jd_copy', 'sourcing_sheet']);
/** «имя.ext: url» целиком, иначе голый URL. */
const FILE_URL_RE = /(?:[^\s:]+\.[a-z0-9]{2,5}\s*:\s*)?https?:\/\/\S+/gi;

export function launchedPreviewText(content: unknown): string {
  const collect = (skipAppendices: boolean): string => {
    const parts: string[] = [];
    const walk = (value: unknown, depth: number): void => {
      if (depth > 4 || parts.join(' ').length > 220) return;
      if (typeof value === 'string') {
        const trimmed = richTextToPlainText(stripWrappingCodeFence(value))
          .replace(FILE_URL_RE, ' ')
          .trim();
        if (trimmed) parts.push(trimmed);
      } else if (Array.isArray(value)) {
        for (const item of value) walk(item, depth + 1);
      } else if (value && typeof value === 'object') {
        for (const [key, item] of Object.entries(value)) {
          if (skipAppendices && depth === 0 && PREVIEW_APPENDIX_KEYS.has(key)) continue;
          walk(item, depth + 1);
        }
      }
    };
    walk(content, 0);
    return parts.join(' ').replace(/\s+/g, ' ').trim();
  };
  const joined = collect(true) || collect(false);
  if (!joined) return '';
  return joined.length > 140 ? `${joined.slice(0, 139)}…` : joined;
}

/** A status badge pill (reuses the shared artifact-status tones). */
function StatusBadge({ type, map }: { type: string; map: ArtifactMap }): JSX.Element {
  const status = artifactStatusOf(map, type);
  const { label, toneClass } = badgeFor(status);
  return (
    <span
      data-testid={`launched-badge-${type}`}
      data-status={status}
      className={`text-xs font-medium px-2 py-0.5 rounded-full ${toneClass}`}
    >
      {label}
    </span>
  );
}

export function LaunchedStageView({
  artifacts,
  onPreview,
  onDownload,
  onSendPackage,
  sendingPackage = false,
}: LaunchedStageViewProps): JSX.Element {
  // The existing (generated) docs among the canonical 6 — the set "Download All"
  // exports and the count that decides whether the button is actionable.
  const existingTypes = GENERATION_CARDS.filter(
    (type) => artifactStatusOf(artifacts, type) !== 'not_generated',
  );

  /** Sequentially export every existing doc via the host's `downloadArtifactDoc`. */
  const handleDownloadAll = (): void => {
    for (const type of existingTypes) onDownload(type);
  };

  return (
    <div data-testid="launched-stage" className="space-y-6">
      {/* "Package Ready for Delivery" banner (legacy delivery hero) + the
          "Send Final Package" action that fulfils its promise (PK-QA-36 §5). */}
      <div
        data-testid="launched-package-banner"
        className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[var(--color-info)]/30 bg-[var(--color-info)]/10 p-4"
      >
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--color-info)]/15 text-[var(--color-info)]"
          >
            <i className="fa-solid fa-box-open" />
          </span>
          <div>
            <h2 className="brand-font text-lg font-bold text-[var(--color-text-strong)]">
              Package Ready for Delivery
            </h2>
            <p className="text-sm text-[var(--color-text-muted)]">
              Send the final package to launch the project.
            </p>
          </div>
        </div>
        <button
          type="button"
          data-testid="launched-send-package"
          disabled={sendingPackage}
          onClick={onSendPackage}
          className="min-h-[40px] px-4 rounded-md bg-[var(--color-primary)] text-white text-sm font-semibold hover:bg-[var(--color-primary-hover)] disabled:opacity-60 inline-flex items-center gap-2"
        >
          {sendingPackage && (
            <span
              data-testid="launched-send-spinner"
              className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"
              aria-hidden="true"
            />
          )}
          <i className="fa-solid fa-paper-plane" aria-hidden />
          Send Final Package
        </button>
      </div>

      {/* Documents — the final package (read-only preview + download + Download All). */}
      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text-strong)]">
            <i className="fa-solid fa-file-lines text-[var(--color-text-muted)]" aria-hidden />
            Documents
          </h3>
          <button
            type="button"
            data-testid="launched-download-all"
            disabled={existingTypes.length === 0}
            onClick={handleDownloadAll}
            className="min-h-[36px] px-3 rounded-md bg-[var(--color-primary)] text-white text-sm font-medium hover:bg-[var(--color-primary-hover)] disabled:opacity-50 inline-flex items-center gap-2"
          >
            <i className="fa-solid fa-download" aria-hidden />
            Download All
          </button>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {GENERATION_CARDS.map((type) => {
            const status = artifactStatusOf(artifacts, type);
            const exists = status !== 'not_generated';
            const preview = exists ? launchedPreviewText(artifacts[type]?.content) : '';
            return (
              <div
                key={type}
                data-testid={`launched-card-${type}`}
                className="flex flex-col gap-3 rounded-lg border border-[var(--color-border)] bg-white p-4"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-[var(--color-text-strong)]">
                    {GENERATION_LABELS[type]}
                  </span>
                  <StatusBadge type={type} map={artifacts} />
                </div>

                {exists ? (
                  <p className="line-clamp-2 text-sm text-[var(--color-text-muted)]">
                    {preview || 'No preview available.'}
                  </p>
                ) : (
                  <p className="text-sm italic text-[var(--color-text-muted)]">
                    Not generated yet.
                  </p>
                )}

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid={`launched-preview-${type}`}
                    disabled={!exists}
                    onClick={() => onPreview(type)}
                    className="min-h-[36px] px-3 rounded-md border border-[var(--color-border)] text-sm hover:bg-[var(--color-surface-muted)] disabled:opacity-50"
                  >
                    <i className="fa-solid fa-eye mr-1.5" aria-hidden />
                    Preview
                  </button>
                  <button
                    type="button"
                    data-testid={`launched-download-${type}`}
                    disabled={!exists}
                    onClick={() => onDownload(type)}
                    className="min-h-[36px] px-3 rounded-md border border-[var(--color-border)] text-sm hover:bg-[var(--color-surface-muted)] disabled:opacity-50"
                  >
                    <i className="fa-solid fa-download mr-1.5" aria-hidden />
                    Download
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

export default LaunchedStageView;
